import { useForm } from "react-hook-form";
import { FC, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "react-router-dom";
import ModalAuxiliares from "./components/ModalAuxiliares";
import {
  AuxiliaresItemSchema,
  ItemSchemaForCounty,
} from "./data/AuxiliaresItemSchema";
import { auxiliaresLookUps } from "./data/AuxiliaresLookUps";
import TableAuxiliaresItems from "./TableAuxiliaresItems";
import { useTableData } from "../../hooks/table/useTableData";
import SearchTable from "../../components/template/Search/SearchTable";
import { useCustomModal } from "../../hooks/modal/useCustomModal";
import { useFilter } from "../../hooks/filter/useFilter";
import SelectComponent from "../../components/molecule/Select/SelectComponent";
import { FilterConfig } from "../../types/Components/Table/FilterConfig";
import { TableConfig } from "../../types/Components/Table/TableConfig";
import { PaginationConfig } from "../../types/Components/Table/PaginationConfig";
import useTableRendering from "../../hooks/table/useTableRendering";
import { PAGE_SIZE_OPTIONS } from "../../utils/constants";
import { ModalConfig } from "../../types/Components/Modal/ModalConfig";
import { useAuth } from "../../provider/AuthProvider";

const AuxiliaresItems: FC = () => {
  const { table } = useParams<{ table: string }>();
  const [idItem, setIdItem] = useState<number>(0);
  const [selectedFilterItem, setSelectedFilterItem] = useState<string>("");
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const { auth } = useAuth();
  const user = auth.user;

  const {
    dataTable,
    originalDataTable,
    parentData,
    formSchema,
    isLoaded,
    errorFindingData,
    setDataTable,
    setOriginalDataTable,
  } = useTableData({
    defaultSchema: AuxiliaresItemSchema,
    lookupTable: auxiliaresLookUps,
    itemSchema: ItemSchemaForCounty,
  });

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
  });

  const { isOpen, title, buttonTitle, openModal, closeModal } = useCustomModal(
    "Criar item",
    "Criar item",
  );

  const { filters, setFilter } = useFilter({
    setValue,
    filterNames: ["provinceId"],
  });

  const filterFunction = (
    item: any,
    searchTerm: string,
    filters: Record<string, string | undefined>,
  ) => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm);
    const matchesProvince =
      !filters["provinceId"] ||
      item.province.id.toString() === filters["provinceId"]?.toString();
    return matchesSearch && matchesProvince;
  };

  const filtersArray = Object.keys(filters)
    .map((filterName) => ({
      key: filterName,
      value: filters[filterName],
      setValue: (value: string | undefined) => setFilter(filterName, value),
    }))
    .filter((filter) => filter.value !== undefined);

  const filterChildren =
    table && auxiliaresLookUps[+table - 1]?.entityName === "County" ? (
      <SelectComponent
        fieldNameObject="provinceId"
        options={parentData}
        fieldName="Província"
        width="230px"
        control={control}
        onChange={(e) => {
          setFilter("provinceId", e.target.value);
          e.target.selectedOptions[0].text !== "Seleccionar" &&
            setSelectedFilterItem(e.target.selectedOptions[0].text);
        }}
        isLoaded={parentData && parentData.length > 0}
      />
    ) : null;

  const lookUpWithParentName =
    (table && auxiliaresLookUps[+table - 1]?.ParentName) || undefined;

  const handleAddNewItem = () => {
    openModal("Criar item", "Criar item");
    setValue("name", "");
    setValue("code", "");
    lookUpWithParentName && setValue("province", "");
  };

  const tableConfig: TableConfig = {
    tableName: "Lista de Itens",
    dataTable,
    originalDataTable,
    setDataTable,
    renderTable: (dataTable) => (
      <TableAuxiliaresItems
        setDataTable={setDataTable}
        setOriginalDataTable={setOriginalDataTable}
        dataTable={dataTable}
        isLoaded={isLoaded}
        openModal={openModal}
        setValue={setValue}
        setIdUpdateItem={setIdItem}
        user={user}
      />
    ),
    isLoaded,
    errorFindingData,
    searchTitle: "Pesquisar pelo Nome do item",
  };

  const filterConfig: FilterConfig = {
    filterFunction,
    filters: filtersArray,
    filterChildren,
    selectedFilterItem: selectedFilterItem,
    setSelectedFilterItem: setSelectedFilterItem,
    hasInputSearch: true,
  };

  const { currentPage, totalPages, handlePageChange, renderContent } =
    useTableRendering(tableConfig, pageSize);

  const paginationConfig: PaginationConfig = {
    pageSize,
    currentPage,
    totalPages,
    handlePageChange,
    renderContent,
    setPageSize,
  };

  const modalConfig: ModalConfig = {
    isOpen,
    title,
    buttonTitle,
    openModal,
    closeModal,
  };

  return (
    <SearchTable
      handleAddNewItem={
        user.permissions.includes("ManageLookups") ? handleAddNewItem : null
      }
      showModal
      searchTableName={table ? auxiliaresLookUps[+table - 1]?.name : "Item"}
      tableConfig={tableConfig}
      filterConfig={filterConfig}
      paginationConfig={paginationConfig}
    >
      <ModalAuxiliares
        setDataTable={setDataTable}
        setOriginalDataTable={setOriginalDataTable}
        control={control}
        errors={errors}
        ParentName={lookUpWithParentName}
        handleSubmit={handleSubmit}
        parentData={parentData}
        idItem={idItem ? idItem.toString() : "1"}
        modalConfig={modalConfig}
        user={user}
      />
    </SearchTable>
  );
};

export default AuxiliaresItems;
